/**
 * HI-SI Dashboard - Formatting Utilities
 */

import { formatDistanceToNow, format, parseISO } from 'date-fns';
import type { StageName, ExecutionStatusType } from '@/lib/shared-types';

// Time formatting
export function formatTimeAgo(timestamp: string | Date): string {
  try {
    const date = typeof timestamp === 'string' ? parseISO(timestamp) : timestamp;
    return formatDistanceToNow(date, { addSuffix: true });
  } catch {
    return 'Unknown time';
  }
}

export function formatDateTime(timestamp: string): string {
  try {
    return format(parseISO(timestamp), 'MMM d, yyyy h:mm:ss a');
  } catch {
    return 'Invalid date';
  }
}

export function formatTime(timestamp: string): string {
  try {
    return format(parseISO(timestamp), 'h:mm:ss a');
  } catch {
    return 'Invalid time';
  }
}

export function formatDate(timestamp: string): string {
  try {
    return format(parseISO(timestamp), 'MMM d, yyyy');
  } catch {
    return 'Invalid date';
  }
}

// Duration formatting
export function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const remainingMinutes = Math.floor((seconds % 3600) / 60);
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  }
}

// Cost formatting
export function formatCost(amount: number): string {
  if (amount < 0.01) {
    return `$0.00`; // Show $0.00 for very small amounts
  } else if (amount < 1) {
    return `$${amount.toFixed(4)}`;
  } else if (amount < 100) {
    return `$${amount.toFixed(2)}`;
  } else {
    return `$${Math.round(amount).toLocaleString()}`;
  }
}

export function formatLargeCost(amount: number): string {
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`;
  } else if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(1)}K`;
  } else {
    return formatCost(amount);
  }
}

// Number formatting
export function formatNumber(num: number): string {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  } else if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  } else {
    return num.toString();
  }
}

export function formatPercentage(value: number, decimals = 1): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

export function formatQualityScore(score: number): string {
  return `${Math.round(score)}/100`;
}

// File size formatting
export function formatFileSize(bytes: number): string {
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 B';
  
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
}

// Stage formatting
export function formatStageName(stage: StageName): string {
  const stageNames: Partial<Record<StageName, string>> = {
    script: 'Script',
    vision: 'Vision',
    storyboard: 'Storyboard',
    shots: 'Shot List',
    continuity: 'Continuity',
    frames: 'Frames',
    videos: 'Video',
    elements: 'Elements',
    qc: 'Quality Control',
    assemble: 'Assembly',
    music: 'Music',
    publish: 'Publish',
    music_master: 'Music Master',
    music_sidechain: 'Music Sidechain',
    export_preview: 'Export Preview'
  };
  
  return stageNames[stage] || stage;
}

export function getStageDescription(stage: StageName): string {
  const descriptions: Partial<Record<StageName, string>> = {
    script: 'Generate script content from curriculum',
    vision: 'Create visual direction and style guide',
    storyboard: 'Plan visual sequence and composition',
    shots: 'Break down into individual shots',
    continuity: 'Ensure visual consistency across shots',
    frames: 'Generate start and end frame images',
    videos: 'Create final video from frames',
    elements: 'Process visual elements',
    qc: 'Quality control review',
    assemble: 'Assemble final output',
    music: 'Generate music track',
    publish: 'Publish content',
    music_master: 'Master music track',
    music_sidechain: 'Create music sidechain',
    export_preview: 'Generate export preview'
  };
  
  return descriptions[stage] || 'Unknown stage';
}

// Status formatting
export function formatExecutionStatus(status: ExecutionStatusType): string {
  const statusNames: Record<ExecutionStatusType, string> = {
    queued: 'Queued',
    running: 'Running',
    paused: 'Paused',
    completed: 'Completed',
    failed: 'Failed',
    cancelled: 'Cancelled'
  };
  
  return statusNames[status] || status;
}

export function getStatusColor(status: ExecutionStatusType): string {
  const colors: Record<ExecutionStatusType, string> = {
    queued: 'text-gray-600',
    running: 'text-primary-600',
    paused: 'text-warning-600',
    completed: 'text-success-600',
    failed: 'text-error-600',
    cancelled: 'text-gray-600'
  };
  
  return colors[status] || 'text-gray-600';
}

export function getStatusBadgeClass(status: ExecutionStatusType): string {
  const classes: Record<ExecutionStatusType, string> = {
    queued: 'badge-gray',
    running: 'badge-primary',
    paused: 'badge-warning',
    completed: 'badge-success',
    failed: 'badge-error',
    cancelled: 'badge-gray'
  };
  
  return classes[status] || 'badge-gray';
}

// Text utilities
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
}

export function capitalizeFirst(text: string): string {
  if (!text) return text;
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

export function camelCaseToTitle(text: string): string {
  return text
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
}

// ID utilities
export function shortenId(id: string, length = 8): string {
  return id.length > length ? `${id.substring(0, length)}...` : id;
}

// Validation utilities
export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

// Array utilities
export function groupBy<T, K extends keyof any>(
  array: T[],
  getKey: (item: T) => K
): Record<K, T[]> {
  return array.reduce((groups, item) => {
    const key = getKey(item);
    (groups[key] = groups[key] || []).push(item);
    return groups;
  }, {} as Record<K, T[]>);
}

export function sortBy<T>(
  array: T[],
  getValue: (item: T) => number | string | Date,
  direction: 'asc' | 'desc' = 'asc'
): T[] {
  return [...array].sort((a, b) => {
    const aVal = getValue(a);
    const bVal = getValue(b);
    
    if (aVal < bVal) return direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return direction === 'asc' ? 1 : -1;
    return 0;
  });
}

// Debounce utility
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

// Throttle utility  
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}